import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './TransporterDashboard.css';

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create custom icons using divIcon - cache instances to avoid recreation
let redIconInstance = null;
let greenIconInstance = null;
let defaultIconInstance = null;

const getRedIcon = () => {
  if (!redIconInstance) {
    redIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #EF4444;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
      ">📍</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }
  return redIconInstance;
};

const getGreenIcon = () => {
  if (!greenIconInstance) {
    greenIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #10B981;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
      ">🎯</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }
  return greenIconInstance;
};

// Create a default icon using divIcon for consistency
const getDefaultIcon = () => {
  if (!defaultIconInstance) {
    defaultIconInstance = L.divIcon({
      className: 'custom-marker-icon',
      html: `<div style="
        width: 30px;
        height: 30px;
        background-color: #3B82F6;
        border: 3px solid white;
        border-radius: 50%;
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        color: white;
        font-weight: bold;
      ">📍</div>`,
      iconSize: [30, 30],
      iconAnchor: [15, 30],
      popupAnchor: [0, -30]
    });
  }
  return defaultIconInstance;
};

function TransporterDashboard() {
  const navigate = useNavigate();
  const [transporterData, setTransporterData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignedRoutes, setAssignedRoutes] = useState([]);
  const [loadingRoutes, setLoadingRoutes] = useState(true);
  const [activeRouteId, setActiveRouteId] = useState(null);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [locationWatchId, setLocationWatchId] = useState(null);

  useEffect(() => {
    // Get transporter data from localStorage
    const storedTransporterData = localStorage.getItem('transporterData');
    if (storedTransporterData) {
      const parsedData = JSON.parse(storedTransporterData);
      setTransporterData(parsedData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (transporterData?.name) {
      fetchAssignedRoutes();
    }
  }, [transporterData?.name]);

  // Cleanup location watch on unmount
  useEffect(() => {
    return () => {
      if (locationWatchId !== null) {
        navigator.geolocation.clearWatch(locationWatchId);
      }
      if (activeRouteId) {
        localStorage.removeItem(`activeRoute_${activeRouteId}`);
      }
    };
  }, [locationWatchId, activeRouteId]);

  const fetchAssignedRoutes = async () => {
    try {
      setLoadingRoutes(true);
      
      // First, find vehicles where driver_name matches the transporter's name
      const { data: vehicles, error: vehiclesError } = await supabase
        .from('transport_vehicles')
        .select('id, bus_number, driver_name')
        .eq('driver_name', transporterData.name);

      if (vehiclesError) {
        console.error('Error fetching vehicles:', vehiclesError);
        setLoadingRoutes(false);
        return;
      }

      if (!vehicles || vehicles.length === 0) {
        setAssignedRoutes([]);
        setLoadingRoutes(false);
        return;
      }

      const vehicleIds = vehicles.map(v => v.id);

      // Fetch routes for these vehicles
      const { data: routesData, error: routesError } = await supabase
        .from('routes')
        .select('*')
        .in('vehicle_id', vehicleIds)
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (routesError) {
        console.error('Error fetching routes:', routesError);
        setLoadingRoutes(false);
        return;
      }

      // Fetch pickup locations for each route
      const routesWithDetails = await Promise.all(
        (routesData || []).map(async (route) => {
          const vehicle = vehicles.find(v => v.id === route.vehicle_id);
          
          // Fetch pickup locations for this route
          const { data: routeLocationData, error: locationError } = await supabase
            .from('route_pickup_locations')
            .select('sequence_order, pickup_location_id')
            .eq('route_id', route.id)
            .order('sequence_order', { ascending: true });

          let pickupLocations = [];
          if (routeLocationData && routeLocationData.length > 0) {
            const locationIds = routeLocationData.map(item => item.pickup_location_id);
            
            const { data: locationDetails, error: detailsError } = await supabase
              .from('pickup_locations')
              .select('id, location_name, address, latitude, longitude')
              .in('id', locationIds);

            if (!detailsError && locationDetails) {
              pickupLocations = routeLocationData.map(routeLoc => {
                const location = locationDetails.find(loc => loc.id === routeLoc.pickup_location_id);
                return location ? {
                  ...location,
                  sequence_order: routeLoc.sequence_order
                } : null;
              }).filter(Boolean);
            }
          }

          return {
            ...route,
            vehicle: vehicle || null,
            pickup_locations: pickupLocations
          };
        })
      );

      setAssignedRoutes(routesWithDetails);
    } catch (err) {
      console.error('Error fetching routes:', err);
    } finally {
      setLoadingRoutes(false);
    }
  };

  const getRouteTypeBadgeColor = (routeType) => {
    switch (routeType) {
      case '1 Way Morning':
        return { bg: '#DBEAFE', color: '#1E40AF' };
      case '1 Way Evening':
        return { bg: '#FEF3C7', color: '#92400E' };
      case '2 Way':
        return { bg: '#D1FAE5', color: '#065F46' };
      default:
        return { bg: '#F3F4F6', color: '#374151' };
    }
  };

  // Geocode address to get coordinates
  const geocodeAddress = async (address, locationName) => {
    try {
      // Try to geocode with location name + address for better accuracy
      const searchQuery = locationName && address 
        ? `${locationName}, ${address}, Rawalpindi, Pakistan`
        : address 
        ? `${address}, Rawalpindi, Pakistan`
        : locationName
        ? `${locationName}, Rawalpindi, Pakistan`
        : null;

      if (!searchQuery) return null;

      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1`,
        {
          headers: {
            'User-Agent': 'TransporterPortal/1.0'
          }
        }
      );
      
      const data = await response.json();
      
      if (data && data.length > 0) {
        return {
          latitude: parseFloat(data[0].lat),
          longitude: parseFloat(data[0].lon)
        };
      }
      return null;
    } catch (error) {
      console.error('Geocoding error:', error);
      return null;
    }
  };

  // Route Map Component
  const RouteMapComponent = ({ locations, isActive = false, userLocation = null, currentStopIndex = null }) => {
    const [geocodedLocations, setGeocodedLocations] = useState([]);
    const [isGeocoding, setIsGeocoding] = useState(false);
    const [geocodingError, setGeocodingError] = useState(false);

    useEffect(() => {
      const fetchCoordinates = async () => {
        // Check if we already have coordinates
        const locationsWithCoords = locations.filter(loc => loc.latitude && loc.longitude);
        
        if (locationsWithCoords.length === locations.length) {
          // All locations have coordinates
          const sorted = [...locations].sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
          setGeocodedLocations(sorted.map(loc => ({
            ...loc,
            latitude: parseFloat(loc.latitude),
            longitude: parseFloat(loc.longitude)
          })));
          return;
        }

        // Need to geocode addresses
        setIsGeocoding(true);
        setGeocodingError(false);

        try {
          const geocoded = await Promise.all(
            locations.map(async (loc) => {
              if (loc.latitude && loc.longitude) {
                return {
                  ...loc,
                  latitude: parseFloat(loc.latitude),
                  longitude: parseFloat(loc.longitude)
                };
              }

              // Geocode the address
              const coords = await geocodeAddress(loc.address, loc.location_name);
              if (coords) {
                return {
                  ...loc,
                  latitude: coords.latitude,
                  longitude: coords.longitude
                };
              }
              return null;
            })
          );

          const validLocations = geocoded.filter(loc => loc !== null);
          
          if (validLocations.length > 0) {
            const sorted = validLocations.sort((a, b) => (a.sequence_order || 0) - (b.sequence_order || 0));
            setGeocodedLocations(sorted);
          } else {
            setGeocodingError(true);
          }
        } catch (error) {
          console.error('Error geocoding:', error);
          setGeocodingError(true);
        } finally {
          setIsGeocoding(false);
        }
      };

      if (locations && locations.length > 0) {
        fetchCoordinates();
      }
    }, [locations]);

    if (isGeocoding) {
      return (
        <div className="map-placeholder">
          <div className="loading-spinner" style={{ margin: '0 auto 10px' }}></div>
          <p>Loading map coordinates...</p>
        </div>
      );
    }

    if (geocodingError || geocodedLocations.length === 0) {
      return (
        <div className="map-placeholder">
          <p>Unable to load map. Please ensure pickup locations have valid addresses.</p>
        </div>
      );
    }

    // Calculate center - include user location if active
    let centerLat, centerLng;
    if (isActive && userLocation) {
      const allPoints = [...geocodedLocations, userLocation];
      centerLat = allPoints.reduce((sum, loc) => sum + (loc.latitude || loc.lat), 0) / allPoints.length;
      centerLng = allPoints.reduce((sum, loc) => sum + (loc.longitude || loc.lng), 0) / allPoints.length;
    } else {
      centerLat = geocodedLocations.reduce((sum, loc) => sum + loc.latitude, 0) / geocodedLocations.length;
      centerLng = geocodedLocations.reduce((sum, loc) => sum + loc.longitude, 0) / geocodedLocations.length;
    }

    // Create polyline coordinates for route
    const polylineCoords = geocodedLocations.map(loc => [
      loc.latitude,
      loc.longitude
    ]);

    // Navigation route from user location to next stop
    let navigationCoords = null;
    if (isActive && userLocation && currentStopIndex !== null && geocodedLocations[currentStopIndex]) {
      const nextStop = geocodedLocations[currentStopIndex];
      navigationCoords = [
        [userLocation.lat || userLocation.latitude, userLocation.lng || userLocation.longitude],
        [nextStop.latitude, nextStop.longitude]
      ];
    }

    return (
      <MapContainer
        center={[centerLat, centerLng]}
        zoom={12}
        style={{ height: '300px', width: '100%', borderRadius: '8px', zIndex: 1 }}
        scrollWheelZoom={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* User location marker (if route is active) */}
        {isActive && userLocation && (
          <Marker
            position={[userLocation.lat || userLocation.latitude, userLocation.lng || userLocation.longitude]}
            icon={getRedIcon()}
          >
            <Popup>
              <div style={{ textAlign: 'center' }}>
                <strong>📍 Your Location</strong>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Markers for each location */}
        {geocodedLocations.map((location, index) => {
          const isNextStop = isActive && currentStopIndex === index;
          // Always provide an icon - use green for next stop, default for others
          const markerIcon = isNextStop ? getGreenIcon() : getDefaultIcon();
          return (
            <Marker
              key={location.id}
              position={[location.latitude, location.longitude]}
              icon={markerIcon}
            >
              <Popup>
                <div style={{ textAlign: 'center' }}>
                  <strong>{location.location_name}</strong>
                  {location.address && (
                    <div style={{ fontSize: '12px', marginTop: '4px', color: '#666' }}>
                      {location.address}
                    </div>
                  )}
                  <div style={{ fontSize: '11px', marginTop: '4px', color: '#FF8B3D', fontWeight: '600' }}>
                    {isNextStop ? '🎯 Next Stop' : `Stop #${index + 1}`}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* Navigation line from user to next stop */}
        {navigationCoords && (
          <Polyline
            positions={navigationCoords}
            color="#3B82F6"
            weight={5}
            opacity={0.8}
            dashArray="10, 10"
          />
        )}
        
        {/* Current stop indicator */}
        {isActive && currentStopIndex !== null && geocodedLocations[currentStopIndex] && (
          <div style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            background: 'white',
            padding: '10px 15px',
            borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            zIndex: 1000,
            fontSize: '14px',
            fontWeight: '600',
            color: '#1F2937'
          }}>
            🎯 Next: {geocodedLocations[currentStopIndex].location_name}
          </div>
        )}
        
        {/* Polyline connecting all locations (only if not in navigation mode) */}
        {!isActive && polylineCoords.length > 1 && (
          <Polyline
            positions={polylineCoords}
            color="#FF8B3D"
            weight={4}
            opacity={0.7}
          />
        )}
      </MapContainer>
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('transporterData');
    navigate('/');
  };

  // Get user's current location
  const getUserLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          reject(error);
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    });
  };

  // Save location to database for students to see
  const saveLocationToDatabase = async (routeId, location) => {
    if (!routeId || !location) return;
    
    try {
      // Store active route location in localStorage for quick access
      const activeRouteData = {
        routeId,
        latitude: location.lat,
        longitude: location.lng,
        timestamp: new Date().toISOString(),
        driverName: transporterData?.name
      };
      localStorage.setItem(`activeRoute_${routeId}`, JSON.stringify(activeRouteData));
      
      // Also try to update in routes table if there's a way to track it
      // For now, we'll use a simple approach with localStorage
      // In production, you might want to create an active_routes table
    } catch (error) {
      console.error('Error saving location:', error);
    }
  };

  // Start route navigation
  const handleStartRoute = async (routeId) => {
    try {
      setLocationError(null);
      const location = await getUserLocation();
      setUserLocation(location);
      setActiveRouteId(routeId);
      setCurrentStopIndex(0);
      
      // Save initial location
      await saveLocationToDatabase(routeId, location);
      
      // Start watching location updates
      if (navigator.geolocation) {
        const watchId = navigator.geolocation.watchPosition(
          (position) => {
            const newLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude
            };
            setUserLocation(newLocation);
            saveLocationToDatabase(routeId, newLocation);
          },
          (error) => {
            console.error('Error watching location:', error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0
          }
        );
        setLocationWatchId(watchId);
      }
    } catch (error) {
      console.error('Error getting location:', error);
      setLocationError('Unable to get your location. Please enable location services.');
      alert('Unable to get your location. Please enable location services in your browser settings.');
    }
  };

  // Go to next stop
  const handleNextStop = () => {
    const activeRoute = assignedRoutes.find(r => r.id === activeRouteId);
    if (activeRoute && activeRoute.pickup_locations) {
      if (currentStopIndex < activeRoute.pickup_locations.length - 1) {
        setCurrentStopIndex(currentStopIndex + 1);
      } else {
        // Route completed
        handleEndRoute();
      }
    }
  };

  // End route
  const handleEndRoute = () => {
    // Stop watching location
    if (locationWatchId !== null) {
      navigator.geolocation.clearWatch(locationWatchId);
      setLocationWatchId(null);
    }
    
    // Clear active route data
    if (activeRouteId) {
      localStorage.removeItem(`activeRoute_${activeRouteId}`);
    }
    
    setActiveRouteId(null);
    setCurrentStopIndex(0);
    setUserLocation(null);
    setLocationError(null);
  };

  if (loading) {
    return (
      <div className="transporter-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!transporterData) {
    return (
      <div className="transporter-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the transporter dashboard.</p>
          <button onClick={() => navigate('/transporter/login')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="transporter-dashboard">
      {/* Top Header Bar */}
      <header className="dashboard-header">
        <div className="header-left">
          <div className="student-info">
            <div className="school-name-header" style={{ color: '#FF8B3D', fontWeight: '900', fontSize: '20px' }}>
              Transporter Portal
            </div>
          </div>
        </div>
        <div className="header-right">
          <div className="user-profile desktop-only">
            <div className="user-avatar">
              {transporterData.name?.charAt(0).toUpperCase() || 'T'}
            </div>
            <div className="user-details-header">
              <div className="user-name-header">{transporterData.name || 'Transporter'}</div>
              <div className="user-role-header">Transporter</div>
            </div>
          </div>
          <button className="logout-btn-header" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </header>

      <main className="dashboard-main">
        <div className="welcome-container">
          <div className="welcome-card">
            <div className="welcome-icon">🚛</div>
            <h2>Welcome, {transporterData.name}!</h2>
            <p>You have successfully logged into the Transporter Dashboard.</p>
            
            <div className="transporter-info">
              <h3>Your Information:</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Name:</span>
                  <span className="info-value">{transporterData.name}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Email:</span>
                  <span className="info-value">{transporterData.email}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Car Number:</span>
                  <span className="info-value">{transporterData.car_number}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Phone:</span>
                  <span className="info-value">{transporterData.phone || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">License:</span>
                  <span className="info-value">{transporterData.license_number || 'Not provided'}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Status:</span>
                  <span className={`info-value status ${transporterData.is_active ? 'active' : 'inactive'}`}>
                    {transporterData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
              </div>
            </div>

            <div className="assigned-routes-section">
              <h3>My Assigned Routes</h3>
              {loadingRoutes ? (
                <div className="routes-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading routes...</p>
                </div>
              ) : assignedRoutes.length === 0 ? (
                <div className="no-routes">
                  <p>No routes have been assigned to you yet.</p>
                </div>
              ) : (
                <div className="routes-list">
                  {assignedRoutes.map((route) => {
                    const badgeColor = getRouteTypeBadgeColor(route.route_type);
                    return (
                      <div key={route.id} className="route-card">
                        <div className="route-header">
                          <div className="route-title-section">
                            <h4>{route.route_name || `Route #${route.id}`}</h4>
                            <span 
                              className="route-type-badge"
                              style={{ 
                                backgroundColor: badgeColor.bg, 
                                color: badgeColor.color 
                              }}
                            >
                              {route.route_type}
                            </span>
                          </div>
                          {route.vehicle && (
                            <div className="route-vehicle">
                              <span className="vehicle-label">Vehicle:</span>
                              <span className="vehicle-number">{route.vehicle.bus_number}</span>
                            </div>
                          )}
                        </div>
                        
                        {route.pickup_locations && route.pickup_locations.length > 0 && (
                          <>
                            <div className="route-locations">
                              <h5>Pickup Locations:</h5>
                              <ol className="locations-list">
                                {route.pickup_locations.map((location, index) => (
                                  <li key={location.id} className="location-item">
                                    <span className="location-name">{location.location_name}</span>
                                    {location.address && (
                                      <span className="location-address">{location.address}</span>
                                    )}
                                  </li>
                                ))}
                              </ol>
                            </div>
                            
                            {/* Route Navigation Buttons */}
                            <div className="route-navigation-buttons">
                              {activeRouteId !== route.id ? (
                                <button 
                                  className="btn-start-route"
                                  onClick={() => handleStartRoute(route.id)}
                                >
                                  🚀 Start Route
                                </button>
                              ) : (
                                <div className="navigation-controls">
                                  <button 
                                    className="btn-next-stop"
                                    onClick={handleNextStop}
                                  >
                                    ➡️ Next Stop
                                  </button>
                                  <button 
                                    className="btn-end-route"
                                    onClick={handleEndRoute}
                                  >
                                    🏁 End Route
                                  </button>
                                </div>
                              )}
                            </div>

                            {/* Route Map */}
                            {route.pickup_locations && route.pickup_locations.length > 0 && (
                              <div className="route-map-container">
                                <h5>Route Map</h5>
                                <RouteMapComponent 
                                  locations={route.pickup_locations}
                                  isActive={activeRouteId === route.id}
                                  userLocation={userLocation}
                                  currentStopIndex={activeRouteId === route.id ? currentStopIndex : null}
                                />
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="dashboard-footer">
        <p>© 2023 Educational Demo. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TransporterDashboard;
